import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Sortable from 'sortablejs';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import ProductHomePageSectionModel from './models/ProductHomePageSectionModel';
import ProductHomePageSectionModal from './ProductHomePageSectionModal';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { logAuditEvent } from '../../../utils/auditLogger';
import {
  buildProductHomePageSectionSavePayload,
  mapProductHomePageSectionFromApi,
} from './utils/mapProductHomePageSection';
import { sortSectionRows, toBooleanFromBit } from './utils/productHomePageSectionHelpers';
import { validateProductHomePageSection } from './productHomePageSectionValidation';

function ProductHomePageSectionGrid({
  SectionData,
  fetchSections,
  UserPermissions = {},
  moduleName = 'Home Page Section',
}) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [sectionModel, setSectionModel] = useState(new ProductHomePageSectionModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [sectionRows, setSectionRows] = useState([]);
  const [orderSaving, setOrderSaving] = useState(false);
  const tableWrapperRef = useRef(null);
  const sortableRef = useRef(null);
  const sectionRowsRef = useRef(sectionRows);

  const canReorder = UserPermissions.EditModule === 1;
  const isFilterActive = filterText.trim().length > 0;

  useEffect(() => {
    sectionRowsRef.current = sectionRows;
  }, [sectionRows]);

  useEffect(() => {
    setSectionRows(sortSectionRows(SectionData || []));
  }, [SectionData]);

  const isRowActive = (row) => toBooleanFromBit(row?.IsActive, false);

  const closeModal = () => {
    setIsOpen(false);
    fetchSections();
  };

  const rows = isFilterActive
    ? sectionRows.filter((item) => {
        const search = filterText.toLowerCase();
        return item.SectionName && item.SectionName.toLowerCase().includes(search);
      })
    : sectionRows;

  const rowOrderKey = useMemo(
    () => rows.map((row) => row.ProductHomePageSectionId).join(','),
    [rows],
  );

  const persistDisplayOrder = useCallback(async (orderedRows, previousRows) => {
    setOrderSaving(true);
    try {
      await axios.post(`${getApiBaseUrl()}/ec-product-homepage-section/update-order`, {
        OrderList: orderedRows.map((row, index) => ({
          ProductHomePageSectionId: row.ProductHomePageSectionId,
          DisplayOrder: index + 1,
        })),
        UserId: getStorageValues('userId'),
      });
      logAuditEvent(moduleName, 'Reorder Sections', `Updated display order for ${orderedRows.length} sections`);
      CreateAlert('Display order updated', 'Success', setAlert);
      await fetchSections();
    } catch (err) {
      console.error(err);
      setSectionRows(previousRows);
      CreateAlert('Unable to update display order', 'Error', setAlert);
    } finally {
      setOrderSaving(false);
    }
  }, [fetchSections, moduleName]);

  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    const container = wrapper?.querySelector('.rdt_TableBody');
    if (!container || !canReorder || isFilterActive) return;

    if (sortableRef.current) {
      sortableRef.current.destroy();
      sortableRef.current = null;
    }

    sortableRef.current = Sortable.create(container, {
      handle: '.drag-handle',
      draggable: '.rdt_TableRow',
      animation: 150,
      scroll: true,
      disabled: orderSaving || loadingDetail,
      onEnd: (evt) => {
        if (orderSaving || evt.oldIndex === evt.newIndex) return;

        const previousRows = sectionRowsRef.current;
        const updated = [...previousRows];
        const [moved] = updated.splice(evt.oldIndex, 1);
        updated.splice(evt.newIndex, 0, moved);
        const withOrder = updated.map((row, index) => ({
          ...row,
          DisplayOrder: index + 1,
        }));

        setSectionRows(withOrder);
        persistDisplayOrder(withOrder, previousRows);
      },
    });

    return () => {
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }
    };
  }, [
    canReorder,
    isFilterActive,
    loadingDetail,
    orderSaving,
    rowOrderKey,
    rows.length,
    persistDisplayOrder,
  ]);

  const loadSectionDetail = async (sectionId) => {
    const response = await axios.get(`${getApiBaseUrl()}/ec-product-homepage-section/getById`, {
      params: { ProductHomePageSectionId: sectionId },
    });
    return mapProductHomePageSectionFromApi(response.data?.data);
  };

  const openActionType = useCallback(async (row, name) => {
    const detail = row?.SectionName ? `Section: ${row.SectionName}` : '';
    logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
    setLoadingDetail(true);
    try {
      const full = row?.ProductHomePageSectionId
        ? await loadSectionDetail(row.ProductHomePageSectionId)
        : row;
      setSectionModel(full || new ProductHomePageSectionModel());
      setActionType(name);
      setIsOpen(true);
    } catch (err) {
      console.error(err);
      CreateAlert('Failed to load section details', 'Error', setAlert);
    } finally {
      setLoadingDetail(false);
    }
  }, [moduleName]);

  const toggleActive = useCallback(async (row) => {
    if (UserPermissions.EditModule !== 1 || !row?.ProductHomePageSectionId) return;

    const nextActive = !isRowActive(row);
    setTogglingId(row.ProductHomePageSectionId);
    logAuditEvent(
      moduleName,
      'Toggle Active',
      `Section ${row.SectionName} set to ${nextActive ? 'active' : 'inactive'}`,
    );

    try {
      const payload = buildProductHomePageSectionSavePayload(
        { ...mapProductHomePageSectionFromApi(row), IsActive: nextActive },
        getStorageValues('userId'),
      );
      const response = await axios.post(`${getApiBaseUrl()}/ec-product-homepage-section/save`, payload);
      const result = response.data?.data;
      if (result?.success === 1) {
        CreateAlert(
          result.message || `Section ${nextActive ? 'activated' : 'deactivated'} successfully!!`,
          'Success',
          setAlert,
        );
        fetchSections();
      } else {
        CreateAlert(result?.message || 'Failed to update section status!!', 'Warning', setAlert);
      }
    } catch (error) {
      console.error(error);
      CreateAlert('Failed to update section status!!', 'Error', setAlert);
    } finally {
      setTogglingId(null);
    }
  }, [UserPermissions.EditModule, fetchSections, moduleName]);

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = buildProductHomePageSectionSavePayload(data, getStorageValues('userId'));
      if (!validateProductHomePageSection(payload, setAlert)) return;
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-homepage-section/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Section updated successfully!!', 'Success', setAlert);
          fetchSections();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to update section!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update section!!', 'Error', setAlert);
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-homepage-section/delete`, {
          ProductHomePageSectionId: sectionModel.ProductHomePageSectionId,
          UserId: getStorageValues('userId'),
        });
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Section deleted successfully!!', 'Success', setAlert);
          fetchSections();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to delete section!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete section!!', 'Error', setAlert);
      }
    }
  };

  const columns = useMemo(() => {
    const baseColumns = [];

    if (canReorder) {
      baseColumns.push({
        name: <div className="titlename" />,
        cell: () => (
          <span
            className="drag-handle"
            style={{ cursor: orderSaving || isFilterActive ? 'not-allowed' : 'grab' }}
            title={isFilterActive ? 'Clear search to reorder' : 'Drag to reorder'}
          >
            <Icons.Menu size={16} />
          </span>
        ),
        width: '30px',
        sortable: false,
      });
    }

    baseColumns.push(
      {
        name: <div className="titlename"><span>Section Name</span></div>,
        selector: (row) => row.SectionName,
        cell: (row) => <span title={row.SectionName}>{row.SectionName}</span>,
        sortable: !canReorder,
        sortIcon: <SortArrow />,
      },
      {
        name: <div className="titlename"><span>Active</span></div>,
        cell: (row) => (
          <div className="status-toggle modal-status">
            <input
              type="checkbox"
              id={`section-active-${row.ProductHomePageSectionId}`}
              className="check"
              checked={isRowActive(row)}
              disabled={UserPermissions.EditModule !== 1 || togglingId === row.ProductHomePageSectionId}
              onChange={() => toggleActive(row)}
            />
            <label htmlFor={`section-active-${row.ProductHomePageSectionId}`} className="checktoggle" />
          </div>
        ),
        width: '100px',
      },
    );

    if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
      baseColumns.push({
        name: <div className="titlename">Actions</div>,
        cell: (row) => (
          <div className="edit-delete-action">
            {UserPermissions.ViewModule === 1 ? (
              <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'View')}>
                <Icons.Eye className="action-eye" />
              </Link>
            ) : null}
            {UserPermissions.EditModule === 1 ? (
              <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'Edit')}>
                <Icons.Edit className="feather-edit" />
              </Link>
            ) : null}
            {UserPermissions.DeleteModule === 1 ? (
              <Link className="confirm-text p-2" to="" onClick={() => openActionType(row, 'Delete')}>
                <Icons.Trash2 className="feather-trash-2" />
              </Link>
            ) : null}
          </div>
        ),
        center: 'true',
        width: '150px',
      });
    }

    return baseColumns;
  }, [
    canReorder,
    isFilterActive,
    openActionType,
    orderSaving,
    toggleActive,
    togglingId,
    UserPermissions.DeleteModule,
    UserPermissions.EditModule,
    UserPermissions.ViewModule,
  ]);

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card" ref={tableWrapperRef}>
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search-section"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            keyField="ProductHomePageSectionId"
            pagination={false}
            defaultSortField="DisplayOrder"
            sortIcon={<SortArrow />}
            persistTableHead
            progressPending={loadingDetail || orderSaving}
          />
        </div>
      </div>
      <ProductHomePageSectionModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={sectionModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductHomePageSectionGrid;
